import { NavigateProvider, useNavigateContext } from '@medely/ui-kit';
import React, { type PropsWithChildren } from 'react';
import { proRoutes, type ProRoutes } from '@mp/routes';
import auth from '@mp/utils/firebase/firebase.web';
import { multiFactor } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { isRunningCypress } from '@mp/utils/testUtils';
import { useCurrentUser } from '../../hooks/useCurrentUser';

export function ProNavigateProvider(props: PropsWithChildren) {
  return <NavigateProvider routes={proRoutes}>{props.children}</NavigateProvider>;
}

export function useProNavigate() {
  return useNavigateContext<ProRoutes>();
}

export function useProNavigateWithMfaRequirement() {
  const { navigate } = useProNavigate();
  const isTwoFactorEnabled = isRunningCypress() ? false : true;

  const firebaseUser = auth.currentUser;
  const mfaInfo = firebaseUser ? multiFactor(firebaseUser) : null;
  const isMfaEnrolled = mfaInfo ? mfaInfo.enrolledFactors.length > 0 : false;
  const { isAgency, isSaas } = useCurrentUser();

  const navigateWithMfaRequirement = <K extends keyof ProRoutes>(
    route: K,
    params?: ProRoutes[K],
    firebaseError?: FirebaseError,
    redirectAfterMfa?: string,
  ) => {
    const firebaseErrorObject = firebaseError
      ? { firebaseError: JSON.stringify(firebaseError) }
      : {};

    const redirectObject = redirectAfterMfa ? { redirectAfterMfa } : {};

    const isImpersonating = window.localStorage.getItem('impersonating') === 'true';
    const bypassMFA = isMfaEnrolled || !isTwoFactorEnabled || isImpersonating || isAgency || isSaas;
    if (bypassMFA) {
      navigate(route as any, params as any);
    } else {
      if (firebaseUser && !firebaseUser.emailVerified) {
        navigate('MfaIdentityVerificationScreen', {
          forward: JSON.stringify({ forwardTo: route, forwardParams: params }) as any,
          ...firebaseErrorObject,
          ...redirectObject,
        });
      } else {
        navigate('MfaSmsCodeEntryScreen', {
          forward: JSON.stringify({ forwardTo: route, forwardParams: params }) as any,
          ...firebaseErrorObject,
          ...redirectObject,
        });
      }
    }
  };

  return {
    navigateWithMfaRequirement,
  };
}
